// Beach volleyball: two against two with a ball flying over the net.
import * as THREE from 'three';
import { PI } from '../../lib/math.js';
import { rnd, rr } from '../../lib/random.js';
import { scene } from '../../core/scene.js';
import { M } from '../../lib/factory.js';
import { volley } from '../crowd.js';
import { wrapAngle } from '../../game/ride.js';
import { toonify } from '../../core/optimise.js';
import { state } from '../../core/state.js';
import { idlers } from './loader.js';
import { AX, AY, makeClip } from './animation.js';
import { beachModel } from './beach-people.js';

export function buildVolleyball() {
  // ---------- beach volleyball: two against two, a real ball flying in arcs over the net ----------
  // 'ready': knees bent, arms forward, weight shifting; 'hit': both arms swing up overhead and down again
  const readyFrame = (rot, f) => {
    rot('Torso', AX, 0.25);
    rot('Torso', AY, 0.08 * Math.sin(f));
    for (const s of ['L', 'R']) {
      rot('UpperLeg.' + s, AX, -0.45 - 0.05 * Math.sin(f));
      rot('LowerLeg.' + s, AX, 0.75 + 0.1 * Math.sin(f));
      rot('Foot.' + s, AX, -0.3);
      rot('UpperArm.' + s, AX, -0.75);
      rot('LowerArm.' + s, AX, -0.35);
    }
  };
  const hitFrame = (rot, f) => {
    const up = Math.sin(f / 2);
    rot('Torso', AX, 0.2 - 0.35 * up);
    for (const s of ['L', 'R']) {
      rot('UpperLeg.' + s, AX, -0.3 * (1 - up));
      rot('LowerLeg.' + s, AX, 0.5 * (1 - up));
      rot('UpperArm.' + s, AX, -0.75 - 2.2 * up);
      rot('LowerArm.' + s, AX, -0.35 * (1 - up));
    }
  };
  const players = volley.map((sp) => {
    const holder = new THREE.Group();
    holder.position.set(sp.x, sp.y, sp.z);
    holder.rotation.y = sp.r;
    scene.add(holder);
    const { model, P } = beachModel(holder, rnd() < 0.5);
    model.position.y = P.y0 * model.scale.y - 0.06;
    P.ready ??= makeClip('Ready', P.root, P.idle, 1.1, readyFrame);
    P.hit ??= makeClip('Hit', P.root, P.idle, 0.55, hitFrame);
    const mixer = new THREE.AnimationMixer(model),
      ready = mixer.clipAction(P.ready),
      hit = mixer.clipAction(P.hit);
    hit.setLoop(THREE.LoopOnce, 1);
    hit.clampWhenFinished = false;
    ready.play();
    ready.time = rnd();
    idlers.push(mixer);
    return {
      holder,
      ready,
      hit,
      home: holder.position.clone(),
      side: sp.z < 57 ? 0 : 1,
      hop: 0,
    };
  });
  const ballMat = M(0xf5c542, { flatShading: false });
  toonify(ballMat);
  const ball = new THREE.Mesh(new THREE.SphereGeometry(0.11, 16, 12), ballMat);
  ball.castShadow = true;
  scene.add(ball);
  const handAt = (pl) => pl.holder.position.clone().add(new THREE.Vector3(0, 1.05, 0));
  state.rally = { from: players[0], to: players[2], t: 0, dur: 1.4 };
  state.updateRally = (dt) => {
    const r = state.rally;
    r.t += dt;
    const u = Math.min(1, r.t / r.dur),
      a = handAt(r.from),
      b = handAt(r.to);
    ball.position.lerpVectors(a, b, u);
    ball.position.y += Math.sin(u * PI) * 2.3;
    ball.rotation.x += dt * 6;
    for (const pl of players) {
      // everyone turns to face the ball; the receiver steps toward where it will land, the others drift home
      const yaw = Math.atan2(
        ball.position.x - pl.holder.position.x,
        ball.position.z - pl.holder.position.z,
      );
      pl.holder.rotation.y += wrapAngle(yaw - pl.holder.rotation.y) * Math.min(1, 4 * dt);
      const goal =
        pl === r.to
          ? new THREE.Vector3(b.x + (pl.home.x - b.x) * 0.4, pl.home.y, pl.home.z)
          : pl.home;
      pl.holder.position.x += (goal.x - pl.holder.position.x) * Math.min(1, 2 * dt);
      pl.hop = Math.max(0, pl.hop - dt);
      pl.holder.position.y =
        pl.home.y + Math.sin(Math.min(1, (0.55 - pl.hop) / 0.55) * PI) * 0.12 * (pl.hop > 0);
    }
    // the receiver starts the swing just before the ball arrives
    if (u > 0.7 && !r.swung) {
      r.swung = true;
      r.to.hit.reset().fadeIn(0.1).play();
      r.to.ready.fadeOut(0.1);
      r.to.hop = 0.55;
    }
    if (u >= 1) {
      r.to.ready.reset().fadeIn(0.25).play();
      r.to.hit.fadeOut(0.25);
      const others = players.filter((p) => p.side !== r.to.side);
      Object.assign(r, {
        from: r.to,
        to: others[Math.floor(Math.random() * others.length)],
        t: 0,
        dur: rr(1.2, 1.6),
        swung: false,
      });
    }
  };
}
